/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import Image from "next/image";
import Login from "../login/login";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import logo from "@/public/images/logo.svg";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import useSizes from "@/lib/window-sizes-hook";
import { Suspense, useEffect } from "react";
import NavbarDrawer from "./navbar-drawer";
import { toggleDrawer } from "@/redux/slices/drawer.slice";

import LanguageSwitch from "../LanguageSwitch";
import { useTranslations } from "next-intl";
import DefaultLink from "next/link";
import { unitTypes } from "@/lib/enums";
import { useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";
import Link from "next/link";
import OwnersDialogs from "./OwnersDialogs";
import NavbarDropdown from "./navbar-dropdown";

const Navbar = () => {
  const { isLoggedIn } = useSelector((state: RootState) => state.login);
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const user = useSelector((state: RootState) => state.user);
  const isClient = useSelector((state: RootState) => state.isBrowser);
  const { maxSm } = useSizes();
  const t = useTranslations();

  const generateQueries = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("classification", value);
    return params.toString();
  };

  useEffect(() => {
    dispatch(toggleDrawer({ isOpen: false }));
  }, [isLoggedIn]);

  if (maxSm && isClient) {
    return (
      <nav className="navbar py-3 px-5 md:px-14 lg:px-20 sticky top-0 z-50 bg-white justify-between sm:shadow-navbar shadow-md">
        {isLoggedIn ? (
          <div className="flex flex-col gap-2 items-start">
            <span className="text-[#969696] text-xs">
              {t("navbarMenu.welcomeBack")}
            </span>
            <span className="text-green-main">
              {user?.firstName ? (
                `${user?.firstName} ${user?.lastName}`
              ) : (
                <Skeleton className="h-5 w-20" />
              )}
            </span>
          </div>
        ) : (
          <Login />
        )}
        <div className="flex gap-1 items-center">
          {/* {isLoggedIn && <ChatWithOwners />} */}
          <NavbarDrawer />
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar py-3 px-5 md:px-14 lg:px-28 sticky top-0 z-20 bg-white justify-between sm:shadow-navbar shadow-md">
      <ul className="flex items-center gap-8 *:text-sm">
        <li>
          <Link aria-label="logo" href={`/`} prefetch className="relative">
            <Image width={114} height={45} alt="logos" src={logo.src} />
          </Link>
        </li>
        <li className="max-lg:hidden">
          <Link
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.hut,
            })}
            href={{
              pathname: `/all-units`,
              query: generateQueries(unitTypes.hut),
            }}
            prefetch
          >
            {t("navbar.items.woodenCabins")}
          </Link>
        </li>
        <li className="max-lg:hidden">
          <Link
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.resort,
            })}
            href={{
              pathname: `/all-units`,
              query: generateQueries(unitTypes.resort),
            }}
            prefetch
          >
            {t("navbar.items.resort")}
          </Link>
        </li>
        <li className="max-lg:hidden">
          <Link
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.chalet,
            })}
            href={{
              pathname: `/all-units`,
              query: generateQueries(unitTypes.chalet),
            }}
            prefetch
          >
            {t("navbar.items.chalets")}
          </Link>
        </li>
        <li className="max-lg:hidden">
          <Link
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.apartment,
            })}
            href={{
              pathname: `/all-units`,
              query: generateQueries(unitTypes.apartment),
            }}
            prefetch
          >
            {t("navbar.items.furnishedApartments")}
          </Link>
        </li>
        <li className="max-lg:hidden">
          <Link
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.room,
            })}
            href={{
              pathname: `/all-units`,
              query: generateQueries(unitTypes.room),
            }}
            prefetch
          >
            {t("navbar.items.hotelRoom")}
          </Link>
        </li>
      </ul>

      <div className="flex items-center gap-2 lg:gap-3">
        {process.env.NEXT_PUBLIC_BUSINESS_URL && (
          <Button
            asChild
            variant={"outline"}
            className="!border-gray-dark text-gray-dark max-md:hidden "
          >
            <DefaultLink
              href={process.env.NEXT_PUBLIC_BUSINESS_URL}
              prefetch
            >
              {t("navbarMenu.addYourProperty")}
            </DefaultLink>
          </Button>
        )}
        {isLoggedIn && isClient ? <NavbarDropdown /> : <Login />}
        {isLoggedIn && isClient ? <OwnersDialogs /> : null}
        <Suspense>
          <LanguageSwitch />
        </Suspense>
      </div>
    </nav>
  );
};

export default Navbar;
